import React, { useState, useEffect, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import { getImageUrl, isCloudinaryUrl, generateCloudinarySrcSet, getCloudinaryUrl } from '../utils/imageUrl';

/**
 * Image presets for different contexts
 * Each preset defines widths for srcset and sizes attribute
 */
const IMAGE_PRESETS = {
  grid: {
    widths: [320, 480, 640, 800, 960],
    sizes: "(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 25vw",
    fallbackWidth: 800
  },
  hero: {
    widths: [640, 800, 960, 1200, 1600],
    sizes: "100vw",
    fallbackWidth: 1200
  },
  pdp: {
    widths: [640, 800, 960, 1200, 1600],
    sizes: "(max-width: 640px) 100vw, (max-width: 1024px) 60vw, 50vw",
    fallbackWidth: 1200
  }
};

/**
 * OptimizedImage Component
 * 
 * Features:
 * - DUAL-READ: Prefers cloudinary_* fields when available
 * - Cloudinary srcset with f_auto, q_auto, dpr_auto, c_limit
 * - Context-aware sizes via presets (grid, hero, pdp)
 * - Lazy loading with Intersection Observer
 * - LCP optimization via fetchPriority="high"
 * - Prevents layout shift with width/height
 * - Loading placeholder
 */
export const OptimizedImage = ({
  src,
  alt,
  className = '',
  width,
  height,
  sizes,                    // Explicit sizes override
  preset = 'grid',          // 'grid' | 'hero' | 'pdp'
  priority = false,
  fetchPriority,            // 'high' | 'low' | 'auto'
  decoding = 'async',
  objectFit = 'cover',
  objectPosition = 'center',
  onLoad,
  onError,
  placeholder = 'blur',
  // DUAL-READ: Accept product object for smart resolution
  product = null,
  cloudinaryUrl = null,
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(priority); // If priority, load immediately
  const [error, setError] = useState(false);
  const imgRef = useRef(null);

  // Intersection Observer for lazy loading
  useEffect(() => {
    if (priority || !imgRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsInView(true);
            observer.disconnect();
          }
        });
      },
      {
        rootMargin: '50px', // Start loading 50px before entering viewport
      }
    );

    observer.observe(imgRef.current);

    return () => observer.disconnect();
  }, [priority]);

  // DUAL-READ STRATEGY: Resolve the best image URL
  // Priority: cloudinaryUrl prop > product.cloudinary_image_url > src
  const resolvedSrc = React.useMemo(() => {
    // 1. Explicit cloudinary URL passed
    if (cloudinaryUrl && isCloudinaryUrl(cloudinaryUrl)) {
      return cloudinaryUrl;
    }
    // 2. Product object with cloudinary field
    if (product?.cloudinary_image_url && isCloudinaryUrl(product.cloudinary_image_url)) {
      return product.cloudinary_image_url;
    }
    // 3. Fall back to src
    return src;
  }, [cloudinaryUrl, product, src]);

  // Get preset configuration
  const presetConfig = IMAGE_PRESETS[preset] || IMAGE_PRESETS.grid;
  
  // Resolve sizes - explicit prop overrides preset
  const resolvedSizes = sizes || presetConfig.sizes;

  // Generate Cloudinary srcset with transformations
  const cloudinarySrcSet = useMemo(() => {
    if (!resolvedSrc) return null;
    return generateCloudinarySrcSet(resolvedSrc, presetConfig.widths);
  }, [resolvedSrc, presetConfig.widths]);

  // Get optimized src (fallback for browsers without srcset support)
  const optimizedSrc = useMemo(() => {
    if (!resolvedSrc) return '';
    if (isCloudinaryUrl(resolvedSrc)) {
      return getCloudinaryUrl(resolvedSrc, presetConfig.fallbackWidth);
    }
    return getImageUrl(resolvedSrc);
  }, [resolvedSrc, presetConfig.fallbackWidth]);

  // Legacy srcset for non-Cloudinary images
  const legacySrcSet = useMemo(() => {
    if (!resolvedSrc || isCloudinaryUrl(resolvedSrc)) return null;
    
    const fullUrl = getImageUrl(resolvedSrc);
    const baseUrl = fullUrl.replace(/_(thumbnail|medium|large)/, '');
    const ext = baseUrl.split('.').pop();
    const basePath = baseUrl.replace(`.${ext}`, '');

    if (basePath.includes('/api/uploads/') || basePath.includes('/uploads/')) {
      return `${basePath}_thumbnail.${ext} 300w, ${basePath}_medium.${ext} 800w, ${basePath}_large.${ext} 1920w`;
    }
    return null;
  }, [resolvedSrc]);

  // Final srcset (Cloudinary or legacy)
  const finalSrcSet = cloudinarySrcSet || legacySrcSet;

  const handleLoad = () => {
    setIsLoaded(true);
    if (onLoad) onLoad();
  };

  const handleError = () => {
    setError(true);
    if (onError) onError();
  };

  // Placeholder while loading
  const placeholderStyle = {
    backgroundColor: '#f3f1ed',
    backgroundImage: placeholder === 'blur' 
      ? 'linear-gradient(90deg, #f3f1ed 0%, #e8e6e1 50%, #f3f1ed 100%)'
      : 'none',
    backgroundSize: '200% 100%',
    animation: placeholder === 'blur' ? 'shimmer 1.5s infinite' : 'none',
  };

  return (
    <div
      ref={imgRef}
      className={`relative overflow-hidden ${className}`}
      style={{
        width: width || '100%',
        height: height || '100%',
        aspectRatio: width && height ? `${width}/${height}` : undefined,
      }}
    >
      {/* Loading placeholder */}
      {!isLoaded && !error && (
        <div
          className="absolute inset-0"
          style={placeholderStyle}
        />
      )}

      {/* Actual image - only render when in view or priority */}
      {isInView && !error && (
        <img
          src={optimizedSrc}
          srcSet={finalSrcSet || undefined}
          sizes={finalSrcSet ? resolvedSizes : undefined}
          alt={alt}
          loading={priority ? 'eager' : 'lazy'}
          fetchPriority={fetchPriority || (priority ? 'high' : undefined)}
          decoding={decoding}
          onLoad={handleLoad}
          onError={handleError}
          style={{
            opacity: isLoaded ? 1 : 0,
            transition: 'opacity 0.3s ease-in-out',
            objectFit: objectFit,
            objectPosition: objectPosition,
          }}
          className="w-full h-full"
        />
      )}

      {/* Error state */}
      {error && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
          <span className="text-gray-400 text-sm">Afbeelding niet beschikbaar</span>
        </div>
      )}
    </div>
  );
};

OptimizedImage.propTypes = {
  src: PropTypes.string,
  alt: PropTypes.string.isRequired,
  className: PropTypes.string,
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  sizes: PropTypes.string,
  preset: PropTypes.oneOf(['grid', 'hero', 'pdp']),
  priority: PropTypes.bool,
  fetchPriority: PropTypes.oneOf(['high', 'low', 'auto']),
  decoding: PropTypes.oneOf(['sync', 'async', 'auto']),
  objectFit: PropTypes.oneOf(['cover', 'contain', 'fill', 'none', 'scale-down']),
  objectPosition: PropTypes.string,
  onLoad: PropTypes.func,
  onError: PropTypes.func,
  placeholder: PropTypes.oneOf(['blur', 'empty']),
  // DUAL-READ props
  product: PropTypes.object,
  cloudinaryUrl: PropTypes.string,
};

export default OptimizedImage;
